import { useEffect, useMemo, useRef } from 'react';

import useUser from '@/hooks/useUser';
import useLearningContents from '@/context/contents/hook';
import useLearningRoutes from '@/context/routes/hook';
import { Content } from '@/services/contents/content.vm';
import { contentsService } from '@/services/contents';

interface Props {
  content?: Content;
  contentId?: string;
  routeId?: string;
}

const useCompleteContent = ({ contentId, content, routeId }: Props) => {
  const preventReFetch = useRef(false);

  const { addPoints } = useUser();
  const { getRouteById, updateRoute } = useLearningRoutes();
  const { markContentAsCompleted, markStageContentAsCompleted } = useLearningContents();

  const userRouteProgressId = useMemo(
    () => getRouteById(routeId as string)?.userRouteProgressId,
    [getRouteById, routeId],
  );

  useEffect(() => {
    if (!contentId || !content || !userRouteProgressId || preventReFetch.current) return;
    preventReFetch.current = true;
    contentsService
      .completeContent({
        contentId: contentId as string,
        userRouteProgressId,
        contentInteractionId: content?.contentInteractionId,
      })
      .then((response) => {
        if (!response) return;

        if (content?.stageId) {
          markStageContentAsCompleted(routeId as string, content?.stageId, contentId as string);
        } else {
          markContentAsCompleted(routeId as string, contentId as string);
        }

        updateRoute(routeId as string, {
          progressStatus: response.routeProgressStatus,
          progress: response.routeProgress,
          stagesProgress: response.stagesProgress,
        });
        addPoints(content?.points ?? 0);
      })
      .catch((error) => {
        console.error('Error completing content:', error);
      });
  }, [contentId, content, routeId, userRouteProgressId]);
};

export default useCompleteContent;
